import React, {useEffect, useState} from 'react';
import Select from 'react-select'
import {client as clientAxios} from "@src/library/axios.library";
import {OfferDiscountTypes, OfferItemForm, OfferItemType, ProductSimply} from "@offer/types/offer.types";
import {toast } from 'react-toastify';
import {Button, Col, Form, InputGroup, Row} from "react-bootstrap";
import {FormError} from "@src/components";

interface OfferProp{
    id: string,
    name?: string
}

const defaultOfferItemFormData: OfferItemForm = {
    id: null,
    product: null,
    discountType: 'percent',
    discountPercent: null,
    discountAmount: null,
    quantity: 1
};

export interface ItemErrorForm {
    product?: [],
    discountType?: [],
    discountValue?: [],
    quantity?: [],
}

interface OfferItemCreateFormProps {
    offer: OfferProp,
    offerItem?: OfferItemType|any,
    refetch?: Function,
    showOfferItemFormHandler: any,
}

const minSelectInputValue:number = 2;

const initOfferItemFormData = (offerItem:OfferItemType|null):OfferItemForm => {
    if (!offerItem) {
        return defaultOfferItemFormData;
    }

    const initProduct = {
        id: offerItem.externalId,
        name: offerItem.name,
        code: offerItem.code,
        priceUnit: parseFloat(offerItem.priceUnitRegular),
        priceUnitRegular: parseFloat(offerItem.priceUnitRegular),
    }

    const offerItemFormData: OfferItemForm = {
        id: offerItem?.id ?? null,
        product: initProduct,
        discountType: offerItem.discountType,
        discountPercent: offerItem?.percentDiscount,// ? parseFloat(offerItem.percentDiscount) : null,
        discountAmount: offerItem?.priceUnitDiscount ? parseFloat(offerItem.priceUnitDiscount) : null,
        quantity: offerItem?.quantity ?? 1,
    };

    return offerItemFormData;
}

const OfferItemFormComponent:React.FC<OfferItemCreateFormProps> =
    ({offer, offerItem, refetch, showOfferItemFormHandler}) => {

    const [productList, setProductList] = useState<ProductSimply[]>([]);
    const [showProductOptions, setShowProductOptions] = useState(minSelectInputValue === 0);
    const [formErrors, setFormErrors] = useState<ItemErrorForm>({});
    const [formData, setFormData] = useState<OfferItemForm>(() => initOfferItemFormData(offerItem));
    const [isSending, setIsSending] = useState<boolean>(false)
    const requestMethod = offerItem?.id ? 'patch' : 'post';
    const requestUrl = offerItem?.id
        ? `/api/v1/offer/${offer.id}/item/external/${offerItem.id}/update`
        : `/api/v1/offer/${offer.id}/item/external/create`;

    const loadProducts = () => {
        clientAxios.get('/api/v1/products/external/simply')
            .then(response => {
                setProductList(response.data.data);
            });
    }

    const setProductHandler = product => {
        setFormData( prevState => {
            return {...prevState, product, discountAmount: 0}
        })
    }

    const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const type = e.target.type;
        let value = null;

        if(type === 'checkbox'){
            value = e.target.checked;
        }else{
            value = e.target.value;
        }

        setFormData(prevState => {
            return {...prevState, ...{[name]: value}}
        });
    }

    const onSubmitHandler = (e) => {
        e.preventDefault();
        setIsSending(true);

        const requestData = {
            product: formData?.product?.id ?? null,
            discountType: formData.discountType === 'percent'
                ? OfferDiscountTypes.Percent : OfferDiscountTypes.Amount,
            discountValue: formData.discountType === 'percent'
                ? formData.discountPercent : formData.discountAmount,
            quantity: formData.quantity,
        };

        clientAxios.request({
            method: requestMethod,
            url: requestUrl,
            data: requestData
        })
            .then(response => {
                refetch();
                toast.success("Zapisano");
                setFormData({...formData, product: null});
                showOfferItemFormHandler(false)
            })
            .catch(error => {
                    setFormErrors(error.response?.data?.errors || {});
            }).finally(() => {
            setIsSending(false);
        })
    }

    useEffect(() => {
        loadProducts();
    }, []);

    const getDiscountedAmount = () => {
        let discountAmount = 0;

        if (formData.product) {
            if ('percent' === formData.discountType) {
                discountAmount = ((formData.discountPercent/100) * formData.product.priceUnit);
                discountAmount = Math.round(discountAmount * 100) / 100;
            } else if ('amount' === formData.discountType) {
                discountAmount = formData.discountAmount;
            }
        }

        let discountedAmount = formData.product.priceUnit - discountAmount;
        if (discountedAmount < 0) { discountedAmount = 0; }

        return discountedAmount;
    }

    return (
        <Form action="" onSubmit={onSubmitHandler}>
            <Form.Group as={Row} className="mb-3">
                <Form.Label column className="text-sm-end required">Produkt:</Form.Label>
                <Col sm={8}>
                    <Select
                        autoFocus={true}
                        value={formData.product}
                        placeholder={'wybierz produkt...'}
                        getOptionLabel={(product) => product.code + ' ' + product.name}
                        getOptionValue={(product) => product.id}
                        onChange={setProductHandler}
                        className={"p-0 form-select " + (formErrors.product ? ' is-invalid' : '')}
                        options={showProductOptions ? productList : []}
                        onInputChange={(typedOption) => setShowProductOptions(typedOption.length >= minSelectInputValue)}
                        styles={{
                            control: (baseStyles, state) => ({
                                ...baseStyles,
                                border: '0',
                            })
                        }}
                        noOptionsMessage={() => (
                            showProductOptions
                                ? 'Nie znalazłem produktu.'
                                : 'Podaj przynajmniej ' + minSelectInputValue + ' znaki, aby wyszukać produkt.'
                        )}
                    />
                    <FormError errors={formErrors.product} />
                </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
                <Form.Label column className="col-form-label text-sm-end">Cena podstawowa:</Form.Label>
                <Col sm={8}>
                    <div className="form-control-plaintext">
                        {formData.product && (
                            <>{formData.product.priceUnit.toFixed(2)} <span className="small text-muted">zł</span></>
                        )}
                    </div>
                </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
                <Form.Label column className="text-sm-end col-form-label- required">Typ rabatu:</Form.Label>
                <Col sm={8}>
                    <div className={formErrors.discountType ? ' is-invalid' : ''}>
                        <div className="form-check form-check-inline">
                            <input className={"form-check-input " + (formErrors.discountType ? ' is-invalid' : '')}
                                   type="radio" name="discountType"
                                   id="discountType-percent"
                                   value={'percent'}
                                   checked={'percent' === formData.discountType}
                                   onChange={onChangeHandler}
                            />
                            <label className="form-check-label"
                                   htmlFor="discountType-percent"
                            >
                                Procentowy
                            </label>
                        </div>
                        <div className="form-check form-check-inline">
                            <input className={"form-check-input " + (formErrors.discountType ? ' is-invalid' : '')}
                                   type="radio" name="discountType"
                                   id="discountType-amount"
                                   value={'amount'}
                                   checked={'amount' === formData.discountType}
                                   onChange={onChangeHandler}
                            />
                            <label className="form-check-label"
                                   htmlFor="discountType-amount"
                            >
                                Kwotowy
                            </label>
                        </div>
                    </div>
                    <FormError errors={formErrors.discountType} />
                </Col>
            </Form.Group>

            {formData.discountType === 'percent' && (
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column className="text-sm-end required">Wartość %:</Form.Label>
                    <Col sm={8}>
                        <InputGroup>
                            <input type="text" name="discountPercent"
                                   className={"form-control " + (formErrors.discountValue ? ' is-invalid' : '')}
                                   value={formData?.discountPercent || ''}
                                   onChange={onChangeHandler}
                            />
                            <span className="input-group-text" id="addon-wrapping">%</span>
                            <FormError errors={formErrors.discountValue} />
                        </InputGroup>
                    </Col>
                </Form.Group>
            )}

            {formData.discountType === "amount" && (
                <Form.Group as={Row} className="mb-3">
                    <Form.Label column className="text-sm-end">Wartość PLN:</Form.Label>
                    <Col sm={8}>
                        <InputGroup>
                            <input type="text" name="discountAmount"
                                   className={"form-control " + (formErrors.discountValue ? ' is-invalid' : '')}
                                   value={formData?.discountAmount || ''}
                                   onChange={onChangeHandler}
                            />
                            <span className="input-group-text" id="addon-wrapping">PLN</span>
                            <FormError errors={formErrors.discountValue} />
                        </InputGroup>
                    </Col>
                </Form.Group>
            )}

            <Form.Group as={Row} className="mb-3">
                <Form.Label column className="text-sm-end">Cena jednostkowa:</Form.Label>
                <Col sm={8}>
                    <div className="form-control-plaintext">
                        {formData.product && (
                            <>{getDiscountedAmount().toFixed(2)} <span className="small text-muted">zł</span></>
                        )}
                    </div>
                </Col>
            </Form.Group>

            <Form.Group as={Row} className="mb-3">
                <Form.Label column className=" text-sm-end required">Ilość:</Form.Label>
                <Col sm={8}>
                    <InputGroup>
                        <input type="text" name="quantity"
                               className={"form-control " + (formErrors.quantity ? ' is-invalid' : '')}
                               value={formData.quantity}
                               onChange={onChangeHandler}
                        />
                        <span className="input-group-text" id="addon-wrapping">szt.</span>
                        <FormError errors={formErrors.quantity} />
                    </InputGroup>
                </Col>
            </Form.Group>

            <Form.Group as={Row}>
                <Form.Label column></Form.Label>
                <Col sm={8}>
                    <Button type="submit" variant={"primary"}>Zapisz produkt</Button>
                </Col>
            </Form.Group>
        </Form>
    );
}

export default OfferItemFormComponent;